import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useApp } from '../../context';
import { canAccess } from '../../utils/rbac';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { fmtDateTimeLong } from '../../utils/dates.js';
import MentionInput, { renderWithMentions } from '../shared/MentionInput';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import SowSelect from '../shared/SowSelect';
import Modal from '../shared/Modal';
import OvertimeActionDecision from './OvertimeActionDecision';
import QmsAcknowledgeAction from './QmsAcknowledgeAction';
import PoInvoicePaidDecision from './PoInvoicePaidDecision';
import CoupaChaseEntry from './CoupaChaseEntry';
import HomeDaysAnswer from './HomeDaysAnswer';
import { SnoozeButton, isSnoozed, setSnooze, snoozeDateLabel } from './ActionSnooze';

// ── Unified Actions queue ────────────────────────────────────────────────────
// One primitive (an "action") covers everything you ask a colleague to do —
// whether raised by @mention under a SOW or sent directly. Two axes: who it
// involves (On me / Raised by me / All) × current vs historical.

const today = () => new Date().toISOString().split('T')[0];

// An approved contractor invoice waiting to go into Sage. Pressing this records
// it on the invoice, which closes the Action for the whole Finance team; it is
// the same button as on Ext. Resource Invoices.
function InvoiceSageEntry({ invoiceId, canDecide, onDecided }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!canDecide || !invoiceId) return null;
  const enter = async () => {
    setBusy(true); setError('');
    try {
      await api.post(`resource-invoices/${invoiceId}/sage-entered`, { entered: true });
      onDecided && onDecided();
    } catch (e) {
      setError(e?.message || 'That could not be saved. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-1.5 flex items-center gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
      <button onClick={enter} disabled={busy}
        className="px-2.5 py-1 text-[9px] font-semibold rounded bg-navy text-white disabled:opacity-40">
        {busy ? 'Saving…' : '✓ Entered in Sage'}</button>
      <span className="text-[9px] text-gray-500">Press once it is in Sage for payment. This closes it for everyone.</span>
      {error && <div className="w-full text-[9px] text-red-600">{error}</div>}
    </div>
  );
}

function StatusPill({ a }) {
  if (a.status === 'resolved') {
    return <span className="text-[7px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">✓ Done</span>;
  }
  // Somebody has picked it up and not yet finished: the others can see it is
  // being handled without opening the thread.
  if (assigneeInfo(a).assignees.some(p => p.started_at && !p.completed_at)) {
    return <span className="text-[7px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">▶ In progress</span>;
  }
  return <span className="text-[7px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">● Open</span>;
}

function DueBadge({ a }) {
  if (!a.due_date) return null;
  const overdue = a.status === 'open' && a.due_date < today();
  return (
    <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded ${overdue ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
      {overdue ? '⚠ due ' : 'due '}{formatDate(a.due_date)}
    </span>
  );
}

// Normalise an action's assignee roster + progress whether it came from the
// queue/overview endpoints (pre-enriched) or the entity list (participants only).
function assigneeInfo(a) {
  const assignees = a.assignees || (a.participants || []).filter(p => p.role === 'assignee');
  const count = a.assignee_count ?? assignees.length;
  const done = a.assignee_done_count ?? assignees.filter(p => p.completed_at).length;
  return { assignees, count, done };
}

// Assignee chips with per-person completion ticks + progress for multi-assignee.
// A QMS acknowledgement goes to the whole company, so its roster ran to dozens
// of names on every card. It keeps the count only; who has and has not
// acknowledged is QMS Control's Acknowledgements register.
function Assignees({ a }) {
  const { assignees, count, done } = assigneeInfo(a);
  if (assignees.length === 0) return null;
  const countOnly = a.entity_type === 'qms_document';
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {(count > 1 || countOnly) && (
        <span className="text-[7px] font-bold text-gray-400" title={a.completion_mode === 'any' ? 'First to complete closes it' : 'Everyone must complete'}>
          {a.completion_mode === 'any' ? '⚡ first of' : '✔ all of'} {done}/{count}
        </span>
      )}
      {!countOnly && assignees.map(p => (
        <span key={p.person_id}
          title={p.completed_at ? 'Done' : p.started_at ? 'Working on it' : undefined}
          className={`text-[7px] px-1.5 py-0.5 rounded-full font-medium ${p.completed_at ? 'bg-emerald-100 text-emerald-700 line-through' : p.started_at ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
          {p.completed_at ? '✓ ' : p.started_at ? '▶ ' : ''}{p.person_name}
        </span>
      ))}
    </div>
  );
}

// ── Mark done modal ──────────────────────────────────────────────────────────
// Completing always needs a note. Rather than a dead disabled button with a
// tooltip, the mark-done buttons open this so the note can be written and the
// action closed in one go.
function CompleteActionModal({ action: a, label, busy, onClose, onComplete }) {
  const [note, setNote] = useState('');
  const canSave = !!note.trim() && !busy;
  return (
    <Modal title={label} onClose={onClose}>
        <div className="text-[9px] text-gray-500 mb-1 truncate">{a.title}</div>
        <p className="text-[9px] text-gray-400 mb-3">An update note is required. The originator sees it as the response.</p>
        <textarea autoFocus className="form-input" rows={4} value={note} onChange={e => setNote(e.target.value)}
          placeholder="What did you do?"
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canSave) onComplete(note.trim()); }} />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 px-3 py-1.5 text-[10px] font-semibold border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={() => onComplete(note.trim())} disabled={!canSave}
            className="flex-1 px-3 py-1.5 text-[10px] font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40">
            {busy ? 'Saving…' : label}
          </button>
        </div>
    </Modal>
  );
}

// ── Edit modal ───────────────────────────────────────────────────────────────
// Open actions are editable by the raiser (or a manager): title, due date,
// visibility and who it's assigned to. Messages stay immutable — the thread is
// the record; this edits the ask, not the conversation.
function EditActionModal({ action: a, people = [], onClose, onSaved }) {
  const [title, setTitle]       = useState(a.title || '');
  const [dueDate, setDueDate]   = useState(a.due_date || '');
  const [visibility, setVisibility] = useState(a.visibility === 'global' ? 'global' : 'named');
  const [assigneeIds, setIds]   = useState(assigneeInfo(a).assignees.map(p => p.person_id));
  const [filter, setFilter]     = useState('');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const activePeople = people.filter(p => !p.employee_status || p.employee_status === 'active');
  const matches = filter
    ? activePeople.filter(p => `${p.first_name} ${p.last_name}`.toLowerCase().includes(filter.toLowerCase())).slice(0, 8)
    : [];
  const toggle = (id) => setIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const nameOf = (id) => {
    const p = people.find(x => x.id === id);
    if (p) return `${p.first_name} ${p.last_name}`;
    const part = (a.participants || a.assignees || []).find(x => x.person_id === id);
    return part?.person_name || id;
  };

  const canSave = !!title.trim() && assigneeIds.length > 0 && !busy;
  const save = async () => {
    if (!canSave) return;
    setBusy(true); setError('');
    try {
      await api.put(`actions/${a.id}`, {
        title: title.trim(),
        due_date: dueDate || null,
        visibility,
        assignee_ids: assigneeIds,
      });
      onSaved && await onSaved();
    } catch (err) {
      setError(err?.message || 'Saving failed. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Edit action" onClose={onClose}>
        <p className="text-[9px] text-gray-400 mb-4">Changes what is being asked. The conversation below the action is not affected.</p>
        <div className="space-y-3">
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">What needs doing? *</label>
            <input autoFocus className="form-input" value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Assigned to *</label>
            {assigneeIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1.5">
                {assigneeIds.map(id => (
                  <span key={id} className="text-[8px] bg-navy/10 text-navy font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1">
                    {nameOf(id)}<button onClick={() => toggle(id)} className="text-navy/50 hover:text-navy">✕</button>
                  </span>
                ))}
              </div>
            )}
            <input className="form-input" placeholder="Type a name to add…" value={filter} onChange={e => setFilter(e.target.value)} />
            {matches.length > 0 && (
              <div className="border border-gray-200 rounded mt-1 max-h-40 overflow-y-auto">
                {matches.map(p => (
                  <button key={p.id} type="button" onClick={() => { toggle(p.id); setFilter(''); }}
                    className="w-full text-left px-2.5 py-1.5 text-[10px] hover:bg-gray-50 flex items-center gap-2">
                    <input type="checkbox" readOnly checked={assigneeIds.includes(p.id)} />
                    {p.first_name} {p.last_name}{p.designation && <span className="ml-auto text-[8px] text-gray-400">{p.designation}</span>}
                  </button>
                ))}
              </div>
            )}
            <p className="text-[8px] text-gray-400 mt-1">Someone removed here keeps the thread but no longer needs to complete it.</p>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Due date</label>
            <input type="date" className="form-input" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Who can see it?</label>
            <div className="space-y-1.5">
              <label className="flex items-start gap-2 text-[10px] cursor-pointer">
                <input type="radio" name="edit-visibility" checked={visibility === 'named'} onChange={() => setVisibility('named')} className="mt-0.5" />
                <span><span className="font-semibold">Only the people named on it</span><span className="text-gray-400"> — it appears in their queues and yours.</span></span>
              </label>
              <label className="flex items-start gap-2 text-[10px] cursor-pointer">
                <input type="radio" name="edit-visibility" checked={visibility === 'global'} onChange={() => setVisibility('global')} className="mt-0.5" />
                <span><span className="font-semibold">Everyone</span><span className="text-gray-400"> — also listed under Company-wide on the Actions page.</span></span>
              </label>
            </div>
          </div>
          {error && <div className="text-[9px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1">{error}</div>}
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 px-3 py-1.5 text-[10px] font-semibold border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={!canSave}
            className="flex-1 px-3 py-1.5 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40">
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
    </Modal>
  );
}

// ── Delete confirmation ──────────────────────────────────────────────────────
// For the duplicate: the same ask raised twice, where resolving the spare one
// records work that never happened and clearing it only hides it from one list.
// Deleting is the only honest answer, and it is irreversible, so it says so
// plainly and the destructive button is not the one under the cursor.
function DeleteActionModal({ action: a, busy, onClose, onDelete, error }) {
  return (
    <Modal title="Delete this action?" onClose={onClose}>
        <div className="text-[9px] text-gray-500 mb-3 break-words">{a.title}</div>
        <p className="text-[10px] text-gray-600 leading-relaxed">
          The action and its whole conversation are removed for everyone named on it, not just from your list.
          This cannot be undone.
        </p>
        <p className="text-[9px] text-gray-400 mt-2">
          Delete a duplicate or something raised in error. If the work was really done, resolve it with a note instead, so the record stands.
        </p>
        {error && <div className="text-[9px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1 mt-2">{error}</div>}
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 px-3 py-1.5 text-[10px] font-semibold border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={onDelete} disabled={busy}
            className="flex-1 px-3 py-1.5 text-[10px] font-semibold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40">
            {busy ? 'Deleting…' : 'Delete action'}
          </button>
        </div>
    </Modal>
  );
}

// ── One action card: collapsed summary + expandable thread ───────────────────
export function ActionCard({ action: a, currentUser, people = [], onChanged, onSnoozeChanged, defaultExpanded = false, showClear = true, highlight = false, answerForOthers = false }) {
  // Interview feedback actions carry the interviewer to the feedback screen.
  const { navigate } = useApp();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [thread, setThread]     = useState(null);
  const [reply, setReply]       = useState('');
  const [replyMentions, setReplyMentions] = useState([]);
  const [busy, setBusy]         = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // The ✕ looked like an instant delete. It never deleted anything (it only
  // hides the action from your own list), but it now asks first either way.
  const [confirmClear, setConfirmClear] = useState(false);
  // Snoozed from this card just now. The card stays where it is and says so,
  // with Undo, rather than vanishing from under the click; the next reload of
  // the queue takes it away. undefined = nothing done here yet.
  const [snoozedHere, setSnoozedHere] = useState(undefined);

  const resolved = a.status === 'resolved';
  const myId = currentUser?.id;
  const { assignees, count: assigneeCount } = assigneeInfo(a);
  const me = assignees.find(p => p.person_id === myId);
  const iAmAssignee = !!me;
  const iCompleted = !!me?.completed_at;
  const iStarted = !!me?.started_at;
  // The ask itself (title, due date, visibility, roster) belongs to the raiser
  // or Action Oversight, mirroring canEditAction on the server.
  const mayOversee = canAccess('action-oversight');
  const mayAdminister = !!myId && (a.created_by === myId || mayOversee);
  // Replying, marking done, resolving and reopening are for somebody on the
  // action (raised it, owns it, assigned, @mentioned or already replied) or
  // Action Oversight, mirroring canActOnAction. Anybody else reaching it, from
  // a SOW's Comments & Actions or the Company-wide list, reads it.
  const onThread = !!myId && (a.created_by === myId || a.owner_id === myId || !!a.my_role
    || (a.participants || []).some(p => p.person_id === myId)
    || (thread?.participants || []).some(p => p.person_id === myId));
  const mayAct = onThread || mayOversee;
  // Snoozing changes only my own participant row, so it needs one.
  const myRow = (a.participants || []).find(p => p.person_id === myId);
  const mySnoozedUntil = snoozedHere !== undefined ? snoozedHere : (a.my_snoozed_until ?? myRow?.snoozed_until ?? null);
  const snoozed = !resolved && isSnoozed(mySnoozedUntil);
  const maySnooze = !resolved && showClear && (!!a.my_role || !!myRow);
  const canEdit = !resolved && mayAdminister;
  // An overtime claim is decided, not completed. Mark done, Resolve all and
  // Delete each closed or removed this card while the claim stayed pending, so
  // the claimant was left waiting on a decision that had stopped being
  // anybody's. Approve and Reject are the only ways past it, and they carry the
  // note forward themselves. Editing is untouched, because an approval waiting
  // on somebody who is away still has to be handed to another manager.
  // A QMS acknowledgement is the same shape: reading the document to the end
  // is what closes a person's part, and Mark done would clear the queue item
  // with no acknowledgement on record. The same guards apply. So does interview
  // feedback: only the feedback screen writes the recommendation to the panel.
  const acknowledgedByReading = a.entity_type === 'qms_document';
  const decidedByFeedback = a.entity_type === 'ta_interview';
  // A supplier invoice paid on a PO closes when the PMO names the milestone it
  // releases, or says there is none.
  const decidedByMilestone = a.entity_type === 'po_invoice_paid';
  // A cardholder saying there is no receipt closes when finance confirm it or ask them to
  // look again on Company Cards, which is what moves the transaction on.
  const decidedOnCardSpend = a.entity_type === 'cot_no_receipt';
  // A contractor's invoice: approving it, or entering it in Sage, is what closes
  // these, so the open ones stay the list of invoices still waiting on finance.
  const invoiceAwaitingApproval = a.entity_type === 'contractor_invoice_approval';
  const invoiceAwaitingSage = a.entity_type === 'contractor_invoice_sage';
  const decidedOnInvoice = invoiceAwaitingApproval || invoiceAwaitingSage;
  // Chasing a milestone invoice's approval on Coupa. Recording the date closes
  // it, but Mark done stays: an invoice that does not go through Coupa is
  // closed with a note saying so.
  const coupaChase = a.entity_type === 'milestone_coupa_chase';
  // Asking for your usual home days: answering it, either way, is what closes it.
  const answeredOnCard = a.entity_type === 'home_days_prompt';
  const decidedByClaim = a.entity_type === 'overtime_request' || acknowledgedByReading || decidedByFeedback || answeredOnCard || decidedByMilestone || decidedOnCardSpend || decidedOnInvoice;
  const canResolveAll = !decidedByClaim;
  // Deleting is offered on resolved actions as well: a duplicate is often only
  // spotted after somebody has closed it, and it should still be able to go.
  // For a claim that is open, though, deleting is the orphaning move.
  const canDelete = mayAdminister && !(decidedByClaim && !resolved);

  const loadThread = useCallback(() => {
    api.get(`actions/${a.id}`).then(setThread).catch(() => setThread({ action: a, messages: [], participants: [] }));
  }, [a.id]);

  useEffect(() => { if (expanded && !thread) loadThread(); }, [expanded, thread, loadThread]);
  // Being linked to while the card is already mounted (a notification clicked
  // from the Actions page itself) must still open it.
  useEffect(() => { if (defaultExpanded) setExpanded(true); }, [defaultExpanded]);

  const refresh = async () => { loadThread(); onChanged && onChanged(); };

  // `noteOverride` comes from the mark-done modal; otherwise the note is
  // whatever is in the inline reply box. A note is mandatory to complete.
  const completeMine = async (done, noteOverride) => {
    const note = (noteOverride ?? reply).trim();
    if (done && !note) { setShowComplete(true); return; }
    setBusy(true);
    try {
      await api.post(`actions/${a.id}/complete`, { complete: done, note: done ? note : undefined });
      if (done) { setShowComplete(false); if (noteOverride === undefined) { setReply(''); setReplyMentions([]); } }
      await refresh();
    } finally { setBusy(false); }
  };
  // In progress is a per-person marker, not a completion: the action stays
  // open, and on a first-to-complete item it tells the others not to start it.
  const startMine = async (start) => {
    setBusy(true);
    try {
      await api.post(`actions/${a.id}/start`, { start });
      await refresh();
    } finally { setBusy(false); }
  };
  const postReply = async ({ resolve } = {}) => {
    if (!reply.trim() && !resolve) return;
    setBusy(true);
    try {
      await api.post(`actions/${a.id}/messages`, {
        body: resolve ? null : (reply.trim() || null),
        mentions: replyMentions,
        resolve: !!resolve,
        resolution_note: resolve ? (reply.trim() || null) : undefined,
      });
      setReply(''); setReplyMentions([]); await refresh();
    } finally { setBusy(false); }
  };
  const reopen = async () => { setBusy(true); try { await api.put(`actions/${a.id}`, { status: 'open' }); await refresh(); } finally { setBusy(false); } };
  const snoozeChanged = (until) => { setSnoozedHere(until); onSnoozeChanged && onSnoozeChanged(); };
  const wake = async () => {
    setBusy(true);
    try { snoozeChanged(await setSnooze(a.id, null)); } catch { /* the badge stays, so nothing claims it woke */ } finally { setBusy(false); }
  };
  const clearMine = async () => { setBusy(true); try { await api.post(`actions/${a.id}/clear`, { clear: true }); onChanged && onChanged(); } finally { setBusy(false); } };
  // Refusals belong on the confirmation the person is looking at, not somewhere
  // else on the page — and the card simply goes on a success.
  const deleteAction = async () => {
    setBusy(true); setDeleteError('');
    try {
      await api.delete(`actions/${a.id}`);
      setConfirmDelete(false);
      onChanged && onChanged();
    } catch (err) {
      setDeleteError(err?.message || 'Deleting failed. Please try again.');
    } finally { setBusy(false); }
  };

  // The same affordance in two places: alongside Edit on an open action, and
  // alongside Reopen on a resolved one.
  const deleteButton = canDelete ? (
    <button onClick={() => { setDeleteError(''); setConfirmDelete(true); }} disabled={busy}
      title="Delete a duplicate or something raised in error — removes it for everyone"
      className="px-2.5 py-1 text-[9px] font-semibold border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-30">🗑 Delete</button>
  ) : null;

  // For multi-assignee 'all' mode, "done" means my part; otherwise it closes it.
  const myDoneLabel = (a.completion_mode === 'all' && assigneeCount > 1) ? '✓ Mark my part done' : '✓ Mark done';

  // Arrived here from a notification or the dashboard: bring it into view and
  // ring it, so it's obvious which of the queue was being pointed at.
  const cardRef = useRef(null);
  useEffect(() => {
    if (highlight && cardRef.current) cardRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlight]);

  return (
    <div ref={cardRef} className={`border rounded bg-white ${resolved ? 'opacity-80' : ''} ${highlight ? 'border-navy ring-2 ring-navy/30' : 'border-gray-200'}`}>
      {/* The whole summary row is the expander, so nothing here looks like a
          control that does something else. */}
      <div className="flex items-start gap-2 px-3 py-2 cursor-pointer" onClick={() => setExpanded(v => !v)}
        role="button" tabIndex={0} aria-expanded={expanded}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(v => !v); } }}>
        <span className="mt-0.5 text-[8px] text-gray-400 flex-shrink-0" aria-hidden="true">
          <span className={`inline-block transition-transform ${expanded ? 'rotate-90' : ''}`}>▶</span>
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <StatusPill a={a} />
            <span className="text-[10px] font-semibold text-navy truncate">{a.title}</span>
            {a.sow_reference && <span className="text-[7px] font-bold text-navy bg-navy/10 px-1.5 py-0.5 rounded">{a.sow_reference}</span>}
            {a.entity_type === 'ta_interview' && a.entity_id && (
              <button onClick={(e) => { e.stopPropagation(); navigate('interview-feedback', a.entity_id); }}
                className="text-[8px] font-bold text-white bg-gold px-1.5 py-0.5 rounded hover:opacity-90"
                title="Open the interview and record your recommendation">
                {resolved ? 'View feedback' : 'Give feedback'}
              </button>
            )}
            {a.entity_type === 'hardware_request' && a.entity_id && (
              <button onClick={(e) => { e.stopPropagation(); navigate('hardware-requests', a.entity_id); }}
                className="text-[8px] font-bold text-white bg-gold px-1.5 py-0.5 rounded hover:opacity-90"
                title="Open the request in Hardware Requests, where it is approved or rejected">
                {resolved ? 'View request' : 'Review request'}
              </button>
            )}
            {a.entity_type === 'cot_no_receipt' && a.entity_id && (
              <button onClick={(e) => { e.stopPropagation(); navigate('card-spend', a.entity_id); }}
                className="text-[8px] font-bold text-white bg-gold px-1.5 py-0.5 rounded hover:opacity-90"
                title="Open the transaction on Company Cards to confirm there is no receipt, or ask them to look again">
                {resolved ? 'View transaction' : 'Decide on Company Cards'}
              </button>
            )}
            {decidedOnInvoice && a.entity_id && (
              <button onClick={(e) => { e.stopPropagation(); navigate('resource-invoices', a.entity_id); }}
                className="text-[8px] font-bold text-white bg-gold px-1.5 py-0.5 rounded hover:opacity-90"
                title={invoiceAwaitingApproval
                  ? 'Open the invoice on Ext. Resource Invoices, where it is approved or rejected'
                  : 'Open the invoice on Ext. Resource Invoices'}>
                {resolved ? 'View invoice' : invoiceAwaitingApproval ? 'Review invoice' : 'Open invoice'}
              </button>
            )}
            {coupaChase && a.milestone_sow_id && (
              <button onClick={(e) => { e.stopPropagation(); navigate('sow-detail', a.milestone_sow_id, `milestone-${a.entity_id}`); }}
                className="text-[8px] font-bold text-white bg-gold px-1.5 py-0.5 rounded hover:opacity-90"
                title="Open the milestone on the opportunity's Milestones tab">
                Open milestone
              </button>
            )}
            {a.bid_reference && <span className="text-[7px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded truncate max-w-[160px]">🎯 {a.bid_reference}</span>}
            {a.milestone_code && <span className="text-[7px] font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">🔗 {a.milestone_code}</span>}
            {a.visibility === 'global' && (
              <span className="text-[7px] font-bold text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded" title="Visible to everyone, not just the people named on it">🌐 Company-wide</span>
            )}
            <DueBadge a={a} />
            {snoozed && snoozedHere === undefined && (
              <span className="text-[7px] font-bold text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded"
                title="Hidden from your list until then. Everyone else on it still sees it.">💤 Snoozed until {snoozeDateLabel(mySnoozedUntil)}</span>
            )}
          </div>
          {/* Just snoozed from here: say what happened where the click was. */}
          {snoozed && snoozedHere !== undefined && (
            <div className="text-[9px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1 mt-1" onClick={e => e.stopPropagation()}>
              Snoozed until {snoozeDateLabel(mySnoozedUntil, true)}. It is off your list until then; everyone else on it still sees it.{' '}
              <button onClick={wake} disabled={busy} className="font-bold underline hover:no-underline disabled:opacity-40">Undo</button>
            </div>
          )}
          <div className="mt-1"><Assignees a={a} /></div>
          {/* An overtime claim is decided here rather than on another screen:
              the action is the thing in the manager's way, so the way past it
              belongs on the action. */}
          {a.entity_type === 'overtime_request' && !resolved && (
            <OvertimeActionDecision
              requestId={a.entity_id}
              canDecide={iAmAssignee && !iCompleted}
              onDecided={() => { refresh(); onChanged && onChanged(); }} />
          )}
          {/* A document to acknowledge is read here, in the same reader Quality
              Docs uses; the acknowledgement completes this person's part. */}
          {decidedByMilestone && !resolved && (
            <PoInvoicePaidDecision
              actionId={a.id}
              canDecide={iAmAssignee && !iCompleted}
              onDecided={() => { refresh(); onChanged && onChanged(); }} />
          )}
          {coupaChase && !resolved && (
            <CoupaChaseEntry
              milestoneId={a.entity_id}
              canDecide={iAmAssignee && !iCompleted}
              onDecided={() => { refresh(); onChanged && onChanged(); }} />
          )}
          {invoiceAwaitingSage && !resolved && (
            <InvoiceSageEntry
              invoiceId={a.entity_id}
              canDecide={iAmAssignee && !iCompleted}
              onDecided={() => { refresh(); onChanged && onChanged(); }} />
          )}
          {/* Action Oversight (answerForOthers) can answer for the person asked. */}
          {answeredOnCard && !resolved && (
            <HomeDaysAnswer
              actionId={a.id}
              canAnswer={a.entity_id === myId || answerForOthers}
              forName={a.entity_id === myId ? null : a.owner_name || 'them'}
              onAnswered={() => { refresh(); onChanged && onChanged(); }} />
          )}
          {acknowledgedByReading && !resolved && (
            <QmsAcknowledgeAction
              documentId={a.entity_id}
              canAcknowledge={iAmAssignee && !iCompleted}
              onAcknowledged={() => { refresh(); onChanged && onChanged(); }} />
          )}
          <div className="flex items-center gap-2 mt-1 flex-wrap text-[8px] text-gray-400">
            <span>From {a.created_by_name || '—'} · {formatDate(a.created_at)}</span>
            {a.message_count > 0 && <span>· 💬 {a.message_count}</span>}
            {resolved && <span className="text-emerald-600 font-semibold">· Resolved {formatDate(a.resolved_at)}{a.resolved_by_name ? ` by ${a.resolved_by_name}` : ''}</span>}
          </div>
          {/* Resolved: surface the response up-front (esp. for the originator on
              "Raised by me") rather than making them expand to find it. */}
          {!expanded && resolved && a.resolution_note && (
            <div className="text-[9px] text-emerald-800 bg-emerald-50 border border-emerald-100 rounded px-2 py-1 mt-1 whitespace-pre-wrap break-words">
              <span className="font-bold uppercase text-[7px] text-emerald-600">Response · {a.resolved_by_name || '—'}</span><br />{a.resolution_note}
            </div>
          )}
          {!expanded && resolved && !a.resolution_note && (
            <div className="text-[8px] text-gray-400 mt-0.5 italic">Resolved with no note.</div>
          )}
          {!expanded && !resolved && a.last_message && (
            <div className="text-[8px] text-gray-400 mt-0.5 truncate italic">{a.last_message}</div>
          )}
        </div>
        {/* Quick controls (collapsed) */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {/* completeMine uses whatever has already been typed in the expanded
              reply box, and opens the note modal only when there is nothing. */}
          {!resolved && iAmAssignee && !iCompleted && !decidedByClaim && (
            <div className="flex items-center gap-1">
              {!iStarted ? (
                <button onClick={e => { e.stopPropagation(); startMine(true); }} disabled={busy}
                  title="Tell everyone on this that you have picked it up"
                  className="px-2 py-1 text-[8px] font-semibold border border-blue-200 text-blue-700 bg-blue-50 rounded hover:bg-blue-100 disabled:opacity-40 whitespace-nowrap">▶ In progress</button>
              ) : (
                <button onClick={e => { e.stopPropagation(); startMine(false); }} disabled={busy}
                  title="You are working on this. Click to put it back down."
                  className="px-2 py-1 text-[8px] font-semibold bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 whitespace-nowrap">▶ In progress</button>
              )}
              <button onClick={e => { e.stopPropagation(); completeMine(true); }} disabled={busy}
                title={reply.trim() ? 'Mark done with the update you have typed' : 'Write an update and mark this done'}
                className="px-2 py-1 text-[8px] font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 whitespace-nowrap">{myDoneLabel}</button>
            </div>
          )}
          {!resolved && iAmAssignee && iCompleted && (
            <span className="text-[7px] font-bold text-emerald-600">✓ Your part done</span>
          )}
          {maySnooze && !snoozed && !confirmClear && (
            <SnoozeButton actionId={a.id} disabled={busy} onSnoozed={snoozeChanged} />
          )}
          {maySnooze && snoozed && snoozedHere === undefined && (
            <button onClick={e => { e.stopPropagation(); wake(); }} disabled={busy}
              title="Put it back on your list now"
              className="px-1.5 py-0.5 text-[8px] font-semibold border border-violet-200 text-violet-700 bg-violet-50 rounded hover:bg-violet-100 disabled:opacity-40 whitespace-nowrap">Wake now</button>
          )}
          {/* The ✕ only hides the action from your own list (nothing is deleted
              or resolved), but it looked like an instant delete — so it asks.
              Not on the usual-home-days question: it is on nobody else, so
              hiding it would drop the question unanswered. Snooze still works. */}
          {!resolved && showClear && !answeredOnCard && !confirmClear && (
            <button onClick={e => { e.stopPropagation(); setConfirmClear(true); }} disabled={busy}
              title="Remove from my list — it stays open for everyone else"
              className="text-[10px] text-gray-300 hover:text-gray-500 leading-none">✕</button>
          )}
          {!resolved && showClear && !answeredOnCard && confirmClear && (
            <div className="flex flex-col items-end gap-1 max-w-[160px] text-right" onClick={e => e.stopPropagation()}>
              <span className="text-[8px] text-gray-500 leading-tight">Remove from your list? It stays open for everyone else — nothing is deleted.</span>
              <div className="flex gap-1">
                <button onClick={() => { setConfirmClear(false); clearMine(); }} disabled={busy}
                  className="px-1.5 py-0.5 text-[8px] font-semibold bg-gray-700 text-white rounded hover:bg-gray-800 disabled:opacity-40">Remove</button>
                <button onClick={() => setConfirmClear(false)}
                  className="px-1.5 py-0.5 text-[8px] font-semibold border border-gray-200 rounded text-gray-500 hover:bg-gray-50">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3 pl-7 border-t border-gray-50">
          {thread === null ? (
            <div className="text-[9px] text-gray-400 py-2">Loading…</div>
          ) : (
            <>
              <div className="space-y-1.5 my-2">
                {(thread.messages || []).map(msg => (
                  <div key={msg.id} className="bg-gray-50 border border-gray-100 rounded px-2 py-1.5">
                    <div className="text-[8px] text-gray-400">{fmtDateTimeLong(msg.created_at)} · <span className="font-semibold text-gray-600">{msg.author_name}</span></div>
                    <div className="text-[10px] text-gray-700 mt-0.5 whitespace-pre-wrap leading-relaxed">{renderWithMentions(msg.body, people)}</div>
                  </div>
                ))}
                {(thread.messages || []).length === 0 && <div className="text-[9px] text-gray-400 italic py-1">No messages yet.</div>}
              </div>

              {resolved && a.resolution_note && (
                <div className="mb-2 text-[9px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1.5">
                  <span className="font-bold uppercase text-[7px] text-emerald-600">Resolution</span><br />{a.resolution_note}
                </div>
              )}

              {!mayAct ? (
                <div className="text-[9px] text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
                  You are not on this action, so you can read it but not reply, mark it done, resolve or reopen it.
                  Ask {a.created_by_name || 'whoever raised it'} to @mention you if you need to join the thread.
                </div>
              ) : !resolved ? (
                <div className="space-y-1.5">
                  <MentionInput value={reply} rows={2} people={people}
                    onChange={(t, ids) => { setReply(t); setReplyMentions(ids); }}
                    placeholder={decidedByClaim
                      ? 'Add a note to the thread… @ someone to loop them in'
                      : 'Add an update / reply… @ someone to loop them in (required to mark done or resolve)'} onSubmit={() => postReply()} />
                  <div className="flex flex-wrap items-center gap-2">
                    {replyMentions.length > 0 && (
                      <span className="text-[8px] text-amber-700 font-semibold">
                        🔔 {[...new Set(replyMentions.map(id => { const p = people.find(x => x.id === id); return p?.first_name; }).filter(Boolean))].join(', ')} will be notified
                      </span>
                    )}
                    <button onClick={() => postReply()} disabled={busy || !reply.trim()}
                      className="ml-auto px-2.5 py-1 text-[9px] font-semibold bg-navy text-white rounded disabled:opacity-30">Reply</button>
                    {iAmAssignee && !iCompleted && !decidedByClaim && (
                      <button onClick={() => startMine(!iStarted)} disabled={busy}
                        title={iStarted ? 'You are working on this. Click to put it back down.' : 'Tell everyone on this that you have picked it up'}
                        className={`px-2.5 py-1 text-[9px] font-semibold rounded disabled:opacity-30 ${iStarted ? 'bg-blue-600 text-white hover:bg-blue-700' : 'border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100'}`}>▶ {iStarted ? 'In progress' : 'Mark in progress'}</button>
                    )}
                    {iAmAssignee && !iCompleted && !decidedByClaim && (
                      <button onClick={() => completeMine(true)} disabled={busy}
                        title={!reply.trim() ? 'Write the update in a popup, then mark done' : undefined}
                        className="px-2.5 py-1 text-[9px] font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-30">{myDoneLabel}</button>
                    )}
                    {iAmAssignee && iCompleted && (
                      <button onClick={() => completeMine(false)} disabled={busy}
                        className="px-2.5 py-1 text-[9px] font-semibold border border-gray-300 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-30">↩ Undo my completion</button>
                    )}
                    {canEdit && (
                      <button onClick={() => setShowEdit(true)} disabled={busy}
                        title="Change the title, due date, visibility or who it's assigned to"
                        className="px-2.5 py-1 text-[9px] font-semibold border border-gray-300 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-30">✎ Edit</button>
                    )}
                    {deleteButton}
                    {canResolveAll && (
                      <button onClick={() => postReply({ resolve: true })} disabled={busy || !reply.trim()}
                        title={!reply.trim() ? 'Add an update above first' : 'Close this for everyone'}
                        className="px-2.5 py-1 text-[9px] font-semibold bg-gray-700 text-white rounded hover:bg-gray-800 disabled:opacity-30">✓ Resolve all</button>
                    )}
                  </div>
                  <div className="text-[7px] text-gray-400">
                    {acknowledgedByReading
                      ? 'Reading the document to the end and acknowledging it closes this for you. A reply here just adds to the thread.'
                      : decidedByFeedback
                      ? 'Giving your feedback on the interview closes this. Use Give feedback above; a reply here just adds to the thread.'
                      : decidedByMilestone
                      ? 'Choosing the milestone this releases, or No milestone needed, closes this. A reply here just adds to the thread.'
                      : decidedOnCardSpend
                      ? 'Confirming there is no receipt, or asking them to look again, on Company Cards closes this. A reply here just adds to the thread.'
                      : answeredOnCard
                      ? (a.entity_id === myId
                          ? 'Saving your home days, or saying you do not have a regular one, closes this. A reply here just adds to the thread.'
                          : 'Saving their home days, or saying they do not have a regular one, closes this. A reply here just adds to the thread.')
                      : decidedByClaim
                      ? 'Approving or rejecting the claim closes this, and the note goes with the decision. A reply here just adds to the thread.'
                      : <>An update note is required to mark done or resolve — {assigneeCount > 1
                          ? (a.completion_mode === 'any'
                              ? 'the first person to mark done closes this for everyone.'
                              : 'it closes automatically once everyone has marked their part done.')
                          : 'the originator sees it as the response.'}</>}
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={reopen} disabled={busy}
                    className="px-2.5 py-1 text-[9px] font-semibold border border-gray-300 text-gray-600 rounded hover:bg-gray-50">↩ Reopen</button>
                  {deleteButton}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {showComplete && (
        <CompleteActionModal action={a} label={myDoneLabel} busy={busy}
          onClose={() => setShowComplete(false)} onComplete={note => completeMine(true, note)} />
      )}

      {showEdit && (
        <EditActionModal action={a} people={people}
          onClose={() => setShowEdit(false)} onSaved={async () => { setShowEdit(false); await refresh(); }} />
      )}

      {confirmDelete && (
        <DeleteActionModal action={a} busy={busy} error={deleteError}
          onClose={() => { setConfirmDelete(false); setDeleteError(''); }} onDelete={deleteAction} />
      )}
    </div>
  );
}

// ── New action modal (the unified "Send Task" / raise) ───────────────────────
// `initial` prefills the form ({ title, details, dueDate }), as My List does
// when a note is turned into an Action. onCreated receives the new Action
// ({ id }), so the caller can link back to it.
export function NewActionModal({ people = [], sows = [], currentUser, onClose, onCreated, defaultEntity, initial }) {
  const [title, setTitle]       = useState(initial?.title || '');
  const [details, setDetails]   = useState(initial?.details || '');
  const [assigneeIds, setIds]   = useState([]);
  const [dueDate, setDueDate]   = useState(initial?.dueDate || '');
  const [error, setError]       = useState('');
  const [sowId, setSowId]       = useState(defaultEntity?.entity_type === 'sow' ? defaultEntity.entity_id : '');
  const [mode, setMode]         = useState('all');
  const [visibility, setVisibility] = useState('named');
  const [filter, setFilter]     = useState('');
  const [busy, setBusy]         = useState(false);

  const activePeople = people.filter(p => !p.employee_status || p.employee_status === 'active');
  const matches = filter
    ? activePeople.filter(p => `${p.first_name} ${p.last_name}`.toLowerCase().includes(filter.toLowerCase())).slice(0, 8)
    : [];
  const toggle = (id) => setIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const nameOf = (id) => { const p = people.find(x => x.id === id); return p ? `${p.first_name} ${p.last_name}` : id; };

  const submit = async () => {
    if (!title.trim() || assigneeIds.length === 0 || busy) return;
    setBusy(true);
    setError('');
    try {
      const payload = {
        title: title.trim(),
        body: details.trim() || null,
        assignee_ids: assigneeIds,
        due_date: dueDate || null,
        completion_mode: assigneeIds.length > 1 ? mode : 'all',
        visibility,
      };
      if (sowId) { payload.entity_type = 'sow'; payload.entity_id = sowId; }
      const created = await api.post('actions', payload);
      onCreated && await onCreated(created);
      onClose();
    } catch (e) {
      setError(e?.message || 'The action could not be raised. Try again.');
    } finally { setBusy(false); }
  };

  return (
    <Modal
      title="New Action"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 text-[10px] font-semibold border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={!title.trim() || assigneeIds.length === 0 || busy}
            className="px-3 py-1.5 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40">
            {busy ? 'Sending…' : 'Raise action'}
          </button>
        </>
      }
    >
        {error && <p className="mb-3 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</p>}
        <p className="text-[9px] text-gray-400 mb-4">Ask one or more colleagues to do something. It lands in their Actions (On me) and notifies them — no email needed.</p>
        <div className="space-y-3">
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">What needs doing? *</label>
            <input autoFocus className="form-input" placeholder="e.g. Review the onboarding doc"
              value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Assign to *</label>
            {assigneeIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1.5">
                {assigneeIds.map(id => (
                  <span key={id} className="text-[8px] bg-navy/10 text-navy font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1">
                    {nameOf(id)}<button onClick={() => toggle(id)} className="text-navy/50 hover:text-navy">✕</button>
                  </span>
                ))}
              </div>
            )}
            <input className="form-input" placeholder="Type a name to add…" value={filter} onChange={e => setFilter(e.target.value)} />
            {matches.length > 0 && (
              <div className="border border-gray-200 rounded mt-1 max-h-40 overflow-y-auto">
                {matches.map(p => (
                  <button key={p.id} type="button" onClick={() => { toggle(p.id); setFilter(''); }}
                    className="w-full text-left px-2.5 py-1.5 text-[10px] hover:bg-gray-50 flex items-center gap-2">
                    <input type="checkbox" readOnly checked={assigneeIds.includes(p.id)} />
                    {p.first_name} {p.last_name}{p.designation && <span className="ml-auto text-[8px] text-gray-400">{p.designation}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          {assigneeIds.length > 1 && (
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Who needs to complete it?</label>
              <div className="space-y-1.5">
                <label className="flex items-start gap-2 text-[10px] cursor-pointer">
                  <input type="radio" name="mode" checked={mode === 'all'} onChange={() => setMode('all')} className="mt-0.5" />
                  <span><span className="font-semibold">Everyone must complete</span><span className="text-gray-400"> — each person does their part; closes when all are done.</span></span>
                </label>
                <label className="flex items-start gap-2 text-[10px] cursor-pointer">
                  <input type="radio" name="mode" checked={mode === 'any'} onChange={() => setMode('any')} className="mt-0.5" />
                  <span><span className="font-semibold">First to complete</span><span className="text-gray-400"> — whoever gets to it first closes it; the rest no longer need to.</span></span>
                </label>
              </div>
            </div>
          )}
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Due date (optional)</label>
            <input type="date" className="form-input" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Who can see it?</label>
            <div className="space-y-1.5">
              <label className="flex items-start gap-2 text-[10px] cursor-pointer">
                <input type="radio" name="visibility" checked={visibility === 'named'} onChange={() => setVisibility('named')} className="mt-0.5" />
                <span><span className="font-semibold">Only the people named on it</span><span className="text-gray-400"> — it appears in their queues and yours.</span></span>
              </label>
              <label className="flex items-start gap-2 text-[10px] cursor-pointer">
                <input type="radio" name="visibility" checked={visibility === 'global'} onChange={() => setVisibility('global')} className="mt-0.5" />
                <span><span className="font-semibold">Everyone</span><span className="text-gray-400"> — also listed under Company-wide on the Actions page for all colleagues.</span></span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Link a SOW (optional)</label>
            <SowSelect sows={sows} value={sowId} onChange={setSowId} placeholder="— None —" />
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Details (optional)</label>
            <textarea className="form-input" rows={2} placeholder="Any context…" value={details} onChange={e => setDetails(e.target.value)} />
          </div>
        </div>
    </Modal>
  );
}

const BOXES  = [
  { id: 'on_me', label: 'On me' },
  { id: 'by_me', label: 'Raised by me' },
  { id: 'all', label: 'All involving me' },
  // Actions whose raiser made them globally visible, whoever they're on.
  { id: 'company', label: 'Company-wide' },
];
const STATES = [{ id: 'open', label: 'Open' }, { id: 'resolved', label: 'Historical' }, { id: 'all', label: 'All' }];

export default function MyActionsPage() {
  // detailId is the action a notification / dashboard link asked us to open
  // (#my-tasks/<action id>).
  const { currentUser, detailId } = useApp();
  const [box, setBox]       = useState('on_me');
  const [status, setStatus] = useState('open');
  const [actions, setActions] = useState(null);
  const [people, setPeople]   = useState([]);
  const [sows, setSows]       = useState([]);
  const [showNew, setShowNew] = useState(false);
  // Cleared as soon as the filters are touched by hand: the highlight means
  // "this is the one you were sent to", which stops being true once you go
  // browsing the queue yourself.
  const [focusId, setFocusId] = useState(detailId || null);
  useEffect(() => { setFocusId(detailId || null); }, [detailId]);

  // What I have snoozed, listed under the open On me queue so it is never
  // hidden past finding: the queue itself leaves snoozed ones out.
  const showsSnoozed = box === 'on_me' && status === 'open';
  const [snoozedList, setSnoozedList] = useState([]);
  const [showSnoozed, setShowSnoozed] = useState(false);
  const loadSnoozed = useCallback(() => {
    if (!showsSnoozed) { setSnoozedList([]); return; }
    api.get('actions/queue', { box: 'on_me', status: 'open', snoozed: 1 })
      .then(a => setSnoozedList(Array.isArray(a) ? a : []))
      .catch(() => setSnoozedList([]));
  }, [showsSnoozed]);

  const load = useCallback(() => {
    api.get('actions/queue', { box, status })
      .then(a => setActions(Array.isArray(a) ? a : []))
      .catch(() => setActions([]));
    loadSnoozed();
  }, [box, status, loadSnoozed]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnFocus(load);

  useEffect(() => {
    api.get('people').then(p => setPeople(Array.isArray(p) ? p : [])).catch(() => {});
    api.get('sows', { status: 'Active' }).then(s => setSows(Array.isArray(s) ? s : [])).catch(() => {});
  }, []);

  // A card snoozed from the list stays on screen until the next reload, but it
  // is already counted under snoozed, so it is not counted twice.
  const openCount = useMemo(() => {
    const snoozedIds = new Set(snoozedList.map(a => a.id));
    return (actions || []).filter(a => a.status === 'open' && !snoozedIds.has(a.id)).length;
  }, [actions, snoozedList]);

  // The linked action may not be in the default box: threads you were only
  // looped into aren't "On me", and a resolved one isn't in the open view. If
  // it's missing, widen the filters once so the thing we were sent to open is
  // actually on screen.
  const widened = useRef(false);
  useEffect(() => { widened.current = false; }, [focusId]);
  useEffect(() => {
    if (!focusId || !actions || widened.current) return;
    if (actions.some(a => a.id === focusId)) return;
    widened.current = true;
    setBox('all');
    setStatus('all');
  }, [focusId, actions]);

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="text-base font-extrabold text-navy">Actions</h2>
          <div className="text-[9px] text-gray-500">Everything you've asked others to do and everything that's on you — one queue. Tag colleagues with @ under a SOW, or raise one here.</div>
        </div>
        <button onClick={() => setShowNew(true)} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">+ New Action</button>
      </div>

      {/* Axis 1: who it involves */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="flex border border-gray-200 rounded overflow-hidden">
          {BOXES.map(b => (
            <button key={b.id} onClick={() => { setBox(b.id); setFocusId(null); }}
              className={`px-3 py-1 text-[9px] font-semibold ${box === b.id ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{b.label}</button>
          ))}
        </div>
        {/* Axis 2: current vs historical */}
        <div className="flex border border-gray-200 rounded overflow-hidden ml-auto">
          {STATES.map(s => (
            <button key={s.id} onClick={() => { setStatus(s.id); setFocusId(null); }}
              className={`px-3 py-1 text-[9px] font-semibold ${status === s.id ? 'bg-gray-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{s.label}</button>
          ))}
        </div>
      </div>

      {status === 'open' && actions && (
        <div className="text-[9px] text-gray-400 mb-2">
          {openCount} open {openCount === 1 ? 'action' : 'actions'}
          {showsSnoozed && snoozedList.length > 0 && <>, plus {snoozedList.length} snoozed</>}
        </div>
      )}

      {/* Followed a link to an action that isn't here even with the filters
          widened — say so rather than showing a list with nothing highlighted. */}
      {focusId && actions && widened.current && !actions.some(a => a.id === focusId) && (
        <div className="text-[9px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1 mb-2">
          That action is no longer in your queue — it may have been resolved or cleared.
        </div>
      )}

      {actions === null ? (
        <div className="text-[10px] text-gray-400 py-10 text-center">Loading…</div>
      ) : actions.length === 0 ? (
        <div className="text-center text-[10px] text-gray-400 py-12 border border-dashed border-gray-200 rounded">
          {box === 'on_me' ? (showsSnoozed && snoozedList.length > 0 ? 'Nothing on you right now, apart from what you have snoozed.' : 'Nothing on you right now. 🎉')
            : box === 'by_me' ? "You haven't raised any actions yet."
            : box === 'company' ? 'No company-wide actions. Anyone can raise one by choosing "Everyone" under "Who can see it?".'
            : 'No actions to show.'}
        </div>
      ) : (
        <div className="space-y-1.5">
          {actions.map(a => (
            <ActionCard key={a.id} action={a} currentUser={currentUser} people={people}
              onChanged={load} onSnoozeChanged={loadSnoozed} showClear={box !== 'by_me' && box !== 'company'}
              highlight={!!focusId && a.id === focusId} defaultExpanded={!!focusId && a.id === focusId} />
          ))}
        </div>
      )}

      {showsSnoozed && snoozedList.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowSnoozed(v => !v)} aria-expanded={showSnoozed}
            className="flex items-center gap-1.5 text-[9px] font-semibold text-violet-700 hover:underline">
            <span className={`inline-block text-[7px] transition-transform ${showSnoozed ? 'rotate-90' : ''}`} aria-hidden="true">▶</span>
            💤 Snoozed ({snoozedList.length})
            <span className="font-normal text-gray-400">Hidden from your list until the date on each. Nobody else's list changes.</span>
          </button>
          {showSnoozed && (
            <div className="space-y-1.5 mt-1.5">
              {snoozedList.map(a => (
                <ActionCard key={a.id} action={a} currentUser={currentUser} people={people}
                  onChanged={load} onSnoozeChanged={load} showClear />
              ))}
            </div>
          )}
        </div>
      )}

      {showNew && (
        <NewActionModal people={people} sows={sows} currentUser={currentUser}
          onClose={() => setShowNew(false)} onCreated={load} />
      )}
    </div>
  );
}
